<template>
  <div>
    <div class="mortgage-result-broker" v-if="offer.success" ref="mortgageResult">
      <div class="mortgage-parameters">
        <img
          style="object-fit: contain"
          :src="offer.bank.logo"
          loading="lazy"
          :alt="offer.extra.bank_code_name"
          class="bank-logo"
        />
        <!-- pozicana suma -->
        <div
          class="bold wrap100px suma amount-editor"
        >
          <input
            v-if="editingAmount"
            ref="amountInput"
            v-model="amountDraft"
            class="amount-editor__input"
            type="number"
            min="1"
            step="100"
            @click.stop
            @keyup.enter="submitAmountEdit"
            @keyup.esc="cancelAmountEdit"
            @blur="submitAmountEdit"
          />
          <button
            v-else
            class="amount-editor__button"
            type="button"
            title="Prepísať výšku hypotéky pre túto banku"
            @click="startAmountEdit"
          >
            {{ offer.pozicana_suma | dotTheAmount }} EUR
          </button>
        </div>
        <!-- fixacia -->
        <div
          class="bold wrap50px fixacia"
        >
          {{ offer.fix }}<span class="upper-index">R</span>
        </div>
        <!-- sadzba -->
        <div
          class="bold wrap50px sadzba"
          v-if="!offer.loadingSadzba"
        >
          {{ offer.sadzba | toFloor(2) }}<span class="upper-index">%</span>
        </div>
        <div
          v-else
          class="bold wrap50px"
        >
          <v-progress-circular
            class="loading-spinner small"
            indeterminate
          ></v-progress-circular>
        </div>
        <!-- mesacna splatka -->
        <div
          class="bold wrap100px splatka"
          v-if="!offer.loadingSplatka"
          style="justify-content: center;"
        >
          <div>{{ offer.mesacna_splatka | dotTheAmount }} <span class="upper-index">EUR</span></div>
          <div class="usetrene" v-if="offer.ucel === 'refinancovanie'">{{ refinancovanieDifference | dotTheAmount }} <span class="upper-index">EUR</span></div>
        </div>
        <div
          v-else
          class="bold wrap50px"
        >
          <v-progress-circular
            class="loading-spinner small"
            indeterminate
          ></v-progress-circular>
        </div>
        <!-- splatnost hypoteky -->
        <div
        >
          <div class="d-flex flex-row align-items-center splatnost">
            <img
              src="https://uploads-ssl.webflow.com/648b04f9d8e52d9524912b4a/648b04f9d8e52d9524912f29_icons8-expand-arrow.svg"
              class="nav-link-icon filter-primary"
              :class="{'disabled-maturity' : offer.splatnost_r === 0}"
              alt=""
              @click="decreaseMaturity"
            />
            <div class="bold wrap50px splatnost" @click="openMaturityInputPopup">
              {{ offer.splatnost_r }} <span class="upper-index">R</span>
            </div>
            <img
              src="https://uploads-ssl.webflow.com/648b04f9d8e52d9524912b4a/648b04f9d8e52d9524912f28_icons8-collapse-arrow.svg"
              class="nav-link-icon filter-primary"
              :class="{'disabled-maturity' : offer.splatnost_r === maxMaturity}"
              alt=""
              @click="increaseMaturity"
            />
          </div>
        </div>
        <!-- spracovatelsky poplatok banky -->
        <div
          class="bold d-flex align-items-center poplatok"
          :class="{'note-present': bankNotes.length !== 0}"
        >
          {{ offer.popl_sprac_hypo | dotTheAmount }} <span class="upper-index">EUR</span>
        </div>
        <!-- poplatok za vedenie uctu
        <div
          class="bold d-flex align-items-center"
          :class="{'note-present': bankNotes.length !== 0}"
        >
          {{ offer.popl_za_vedenie_uctu }} EUR<span class="upper-index">/ m</span>
        </div> -->
      </div>
      <div v-for="warning in (offer.extra.inactive_rate_changes || [])" :key="'inactive-' + warning.id"
        class="ltv-90-notice" style="flex-direction: column;">
        <strong>Zobrazená je ponuka bez vybranej zľavy</strong>
        <span>{{ warning.reasons.join(' ') }} Produkty môžete nastaviť nad výsledkami bánk alebo zľavu zrušiť nižšie.</span>
      </div>
      <div
        v-if="ltv90Notice"
        class="ltv-90-notice"
        :class="{'ltv-90-notice--missing': ltv90Notice.missing_rate_change}"
      >
        <strong>90 % LTV prirážka: {{ ltv90SurchargeLabel }}</strong>
        <span>{{ ltv90NoticeText }}</span>
      </div>
      <div class="discount-list">
        <discount-item
          v-for="(discount, key) in offer.extra.mozne_zmeny_sadzieb"
          :key="key"
          :discount="discount"
          :mortgage-rate-changes="mortgageRateChanges"
          :mortgage-id="mortgageId"
          @discount-change="handleSadzbaChange"
        />
      </div>
      <div class="discount-list">
        <div
          class="discount-item kampane"
          v-for="(noteObject, index) in ($store.getters['formStore/getBankByKey'](offer.bank.code)).notes"
          :key="index"
        >
          {{ noteObject.note }}
        </div>
      </div>
      <div class="offer-actions">
        <button type="button" @click="createApplication()" class="offer-action offer-action--primary">
          <span v-if="!loading">
            Vybrať
          </span>
          <v-progress-circular
            v-else
            class="loading-spinner extrasmall offer-action__loader"
            indeterminate
          ></v-progress-circular>
        </button>
        <button type="button" class="offer-action" @click="showCampaign(offer.bank.code)">
          Kampane
        </button>
        <button type="button" class="offer-action" @click="openHelperPopup(offer.bank.code)">
          Metodika
        </button>
        <client-dti-info-handler
          action-button
          :bank-key="offer.extra.bank_code_name"
          :offer="offer"
          :rules="['priority']"
        />
        <button type="button" class="offer-action" @click="copyToClipboard">
          Kopírovať
        </button>
      </div>
    </div>
    <div class="mortgage-result-broker error-offer" v-else>
      <img
        :src="offer.bank.logo"
        width="35"
        height="35"
        loading="lazy"
        alt=""
        class="bank-logo"
      />
      <div>
        <div
          class="error-message blc-color"
          v-for="(error, key) in offer.details"
          :key="key"
        >
          {{ error }}
        </div>
        <div v-if="!offer.details || offer.details.length === 0" class="error-message blc-color">
          Banka pre zadané parametre nevrátila ponuku ani podrobnejší dôvod.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DiscountItem from "./DiscountItem";
import { createMortgageApplication, deleteMortgageApplicationByID } from "@/services/brokerService";
import translateFromDBValue from "../../../../../mixins/translateFromDBValue";
import mortgageUtils from "../../../../../mixins/mortgageUtils";
import ClientDtiInfoHandler from "@/components/shared/PopupHandler/Popups/ClientDtiInfo/ClientDtiInfoHandler.vue";

export default {
  name: "MortgageApplicationChoiceItem",
  components: {
    ClientDtiInfoHandler,
    DiscountItem
  },
  props: {
    offer: Object,
    bankKey: String,
  },
  mixins: [
    translateFromDBValue,
    mortgageUtils,
  ],
  computed: {
    ...mapGetters({
      maturityGetter: "mortgageDetailState/getMaturity",
      ratesGetter: "mortgageDetailState/getAppliedMortgageRateChanges",
      // pridane cez gpt pre vypocet rozdielu splatky 18_09
      getRefinancedDebts: "mortgageDetailState/getRefinancedDebts"
    }),

    // pridane cez GPT 18_09 aby som počítal rozdiel splátky
    // súčet starých splátok
    oldMonthlyPayment() {
      const debts = this.getRefinancedDebts(this.mortgageId) || [];
      return debts.reduce((sum, debt) => sum + (debt.vyska_splatky || 0), 0);
    },

    // nová splátka z ponuky
    newMonthlyPayment() {
      return this.offer?.mesacna_splatka || 0;
    },

    // rozdiel medzi starými a novou
    refinancovanieDifference() {
      return this.oldMonthlyPayment - this.newMonthlyPayment;
    },

    ltv90Notice() {
      const info = this.offer?.extra?.ltv_90_prirazka;
      if (!info || (!info.applied && !info.missing_rate_change && !info.waived)) return null;
      return info;
    },

    ltv90SurchargeLabel() {
      if (this.ltv90Notice?.missing_rate_change) return "neurčená";
      const amount = this.ltv90Notice?.surcharge_percentage_points ??
        (this.ltv90Notice?.changes || []).reduce((sum, change) => sum + Number(change.zmena || 0), 0);
      return `${Number(amount) > 0 ? "+" : ""}${Number(amount).toFixed(2).replace(".", ",")} p. b.`;
    },

    ltv90NoticeText() {
      if (!this.ltv90Notice) return "";
      if (this.ltv90Notice.waived) {
        return `LTV ${this.formatLtv(this.ltv90Notice.requested_ltv)} presahuje 80 %, ale podľa podmienok banky alebo kampane sa prirážka neuplatňuje. Sadzba sa preto nezvyšuje.`;
      }
      if (this.ltv90Notice.missing_rate_change) {
        return `LTV ${this.formatLtv(this.ltv90Notice.requested_ltv)} presahuje 80 %, ale banka nemá zadanú príslušnú úpravu sadzby.`;
      }

      const totalChange = (this.ltv90Notice.changes || [])
        .reduce((sum, change) => sum + Number(change.zmena || 0), 0);
      const changeText = totalChange
        ? ` Sadzba je upravená o +${totalChange.toFixed(2)} p. b.`
        : "";
      if (this.ltv90Notice.source === "automatic") {
        return `LTV ${this.formatLtv(this.ltv90Notice.requested_ltv)} presahuje 80 %, preto sa prirážka uplatnila automaticky.${changeText}`;
      }
      return `Prirážka bola zapnutá hromadným nastavením aj bez zvolenej nehnuteľnosti.${changeText}`;
    }
  },
  watch: {
    "offer.pozicana_suma"(newValue) {
      if (!this.editingAmount) {
        this.amountDraft = Math.round(Number(newValue) || 0);
      }
    },
    maturityGetter(newMaturity) {
      const purpose = this.getMortgagePurpose();
      const maturityObject = newMaturity[this.offer.bank.code][purpose];
      this.maxMaturity = maturityObject.success ? maturityObject.value : 0;
    },
    ratesGetter (newRates) {
      this.mortgageRateChanges = newRates;
    },
  },
  created () {
    // const notes = ;
    // for (const note of notes) {
    //   console.log(note)
    //   this.bankNotes.push(note.note);
    // }
    this.mortgageId = this.$store.getters["mortgageDetailState/getMortgage"].id;
    this.mortgageRateChanges = this.ratesGetter;
    this.amountDraft = Math.round(Number(this.offer.pozicana_suma) || 0);
    const purpose = this.getMortgagePurpose();
    const maturityObject = (this.$store.getters["mortgageDetailState/getMaturity"])[this.offer.bank.code][purpose];
    this.maxMaturity = maturityObject.success ? maturityObject.value : 0;
  },
  methods: {
    getOfferKey() {
      return {
        bankKey: this.offer.extra.bank_code_name,
        fix: this.offer.fix,
        splatnost_r: this.offer.splatnost_r,
        ucel: this.offer.ucel,
      };
    },
    startAmountEdit() {
      this.amountDraft = Math.round(Number(this.offer.pozicana_suma) || 0);
      this.editingAmount = true;
      this.$nextTick(() => {
        const input = this.$refs.amountInput;
        if (!input) return;
        input.focus();
        input.select();
      });
    },
    cancelAmountEdit() {
      this.amountDraft = Math.round(Number(this.offer.pozicana_suma) || 0);
      this.editingAmount = false;
    },
    getAmountsForManualValue(value) {
      return {
        suma_nadobudnutie: this.offer.ucel === "nadobudnutie" ? value : 0,
        suma_refinancovanie: this.offer.ucel === "refinancovanie" ? value : 0,
        suma_bezucel: this.offer.ucel === "bezucel" ? value : 0,
      };
    },
    submitAmountEdit() {
      if (!this.editingAmount) return;

      const amount = Math.round(Number(this.amountDraft));
      this.editingAmount = false;

      if (!amount || amount <= 0) {
        this.cancelAmountEdit();
        this.$store.commit("appState/setSuccessMessage", {
          text: "Zadajte platnú výšku hypotéky.",
          type: "error"
        });
        return;
      }

      if (amount === Math.round(Number(this.offer.pozicana_suma) || 0)) {
        this.amountDraft = amount;
        return;
      }

      this.amountDraft = amount;
      this.$store.dispatch("mortgageDetailState/fetchOffer", {
        mortgageId: this.mortgageId,
        bankKey: this.offer.bank.code,
        loadingSplatka: true,
        loadingSadzba: true,
        offerKey: this.getOfferKey(),
        offerMeta: {
          bank: this.translateBankFromCodeNameToId(this.offer.extra.bank_code_name),
          fix: this.offer.fix,
          ...this.getAmountsForManualValue(amount),
          splatnost_m: this.offer.splatnost_r * 12
        }
      });
    },
    openMaturityInputPopup() {
      const amounts = this.$store.getters["mortgageDetailState/getAmounts"];
      const offerPayload = {
        mortgageId: this.mortgageId,
        bankKey: this.offer.bank.code,
        loadingSplatka: true,
        loadingSadzba: false,
        offerKey: this.getOfferKey(),
        offerMeta: {
          bank: this.translateBankFromCodeNameToId(this.offer.extra.bank_code_name),
          fix: this.offer.fix,
          ...amounts,
          splatnost_m: (this.offer.splatnost_r) * 12
        }
      };
      this.$store.commit("popupManagerState/pushToPopupOrder", {
        type: null,
        payload: offerPayload,
        component: "MaturityChoosePopUp",
        rules: ['priority'],
      })
    },
    showCampaign(bankKey) {
      switch(bankKey) {
        case "365": window.open('https://bankari.sk/hypoteka/365-bank', '_blank'); break;
        case "tatra": window.open('https://bankari.sk/hypoteka/tatra-banka', '_blank'); break;
        case "csob": window.open('https://bankari.sk/hypoteka/csob-banka', '_blank'); break;
        case "unicredit": window.open('https://bankari.sk/hypoteka/unicreditbank', '_blank'); break;
        case "slsp": window.open('https://bankari.sk/hypoteka/slovenska-sporitelna', '_blank'); break;
        case "vub": window.open('https://bankari.sk/hypoteka/vub-banka', '_blank'); break;
        case "prima": window.open('https://bankari.sk/hypoteka/prima', '_blank'); break;
        case "mbank": window.open('https://bankari.sk/hypoteka/mbank', '_blank'); break;
      }
    },
    decreaseMaturity() {
      const amounts = this.$store.getters["mortgageDetailState/getAmounts"];
      this.$store.dispatch("mortgageDetailState/fetchOffer", {
        mortgageId: this.mortgageId,
        bankKey: this.offer.bank.code,
        loadingSplatka: true,
        loadingSadzba: false,
        offerKey: this.getOfferKey(),
        offerMeta: {
          bank: this.translateBankFromCodeNameToId(this.offer.extra.bank_code_name),
          fix: this.offer.fix,
          ...amounts,
          splatnost_m: (this.offer.splatnost_r - 1) * 12
        }
      });
    },
    increaseMaturity() {
      const amounts = this.$store.getters["mortgageDetailState/getAmounts"];
      this.$store.dispatch("mortgageDetailState/fetchOffer", {
        mortgageId: this.mortgageId,
        bankKey: this.offer.bank.code,
        loadingSplatka: true,
        loadingSadzba: false,
        offerKey: this.getOfferKey(),
        offerMeta: {
          bank: this.translateBankFromCodeNameToId(this.offer.extra.bank_code_name),
          fix: this.offer.fix,
          ...amounts,
          splatnost_m: (this.offer.splatnost_r + 1) * 12
        }
      });
    },
    handleSadzbaChange() {
      this.$store.dispatch("mortgageDetailState/fetchOffersByBank", {
        mortgageId: this.mortgageId,
        bankKey: this.offer.bank.code,
        loadingSplatka: true,
        loadingSadzba: true,
      });
    },
    formatLtv(value) {
      const number = Number(value);
      return Number.isFinite(number) ? `${number.toFixed(2)} %` : "nad 80 %";
    },
    // metóda na kopírovanie ponuky do mailu
    async copyToClipboard() {
      const offer = this.$refs.mortgageResult;

      if (!offer) return;

      // Get bank code for fallback alt text
      const bankCode = offer.querySelector('.bank-logo')?.alt || 'Unknown Bank';
      // Determine campaign link based on bankCode
      const campaignLinks = {
        "365": "https://bankari.sk/hypoteka/365-bank",
        "tatra": "https://bankari.sk/hypoteka/tatra-banka",
        "csob": "https://bankari.sk/hypoteka/csob-banka",
        "unicredit": "https://bankari.sk/hypoteka/unicreditbank",
        "slsp": "https://bankari.sk/hypoteka/slovenska-sporitelna",
        "vub": "https://bankari.sk/hypoteka/vub-banka",
        "prima": "https://bankari.sk/hypoteka/prima",
        "mbank": "https://bankari.sk/hypoteka/mbank"
      };
      const campaignLink = campaignLinks[bankCode] || '';

      // Construct an HTML table from offer data
      let tableHTML = `
        <table border="1" cellspacing="0" cellpadding="5" style="border-collapse: collapse; text-align: left; border: 1px solid grey;">
          <tr style="background-color: #f0f0f0; border: 1px solid grey;">
            <th style="border: 1px solid grey;">Banka</th>
            <th style="border: 1px solid grey;">Suma</th>
            <th style="border: 1px solid grey;">Sadzba</th>
            <th style="border: 1px solid grey;">Splátka</th>
            <th style="border: 1px solid grey;">Poplatok</th>
            <th style="border: 1px solid grey;">Splatnosť</th>
            <th style="border: 1px solid grey;">Fixácia</th>
          </tr>
          <tr style="border: 1px solid grey;">
            <td style="border: 1px solid grey; text-transform: uppercase; font-weight: bold;">${bankCode}</td>
            <td style="border: 1px solid grey;">${offer.querySelector('.suma')?.textContent.trim() || ''}</td>
            <td style="border: 1px solid grey;">${offer.querySelector('.sadzba')?.textContent.trim() || ''}</td>
            <td style="border: 1px solid grey;">${offer.querySelector('.splatka')?.textContent.trim() || ''}</td>
            <td style="border: 1px solid grey;">${offer.querySelector('.poplatok')?.textContent.trim() || ''}</td>
            <td style="border: 1px solid grey;">${offer.querySelector('.splatnost')?.textContent.trim() || ''}</td>
            <td style="border: 1px solid grey;">${offer.querySelector('.fixacia')?.textContent.trim() || ''}</td>
          </tr>
        </table>
      `;

      const ltvNotice = offer.querySelector('.ltv-90-notice');
      if (ltvNotice) {
        tableHTML += `<p><strong>${ltvNotice.textContent.trim()}</strong></p>`;
      }

      // Include discount items
      const discountItems = offer.querySelectorAll('.discount-item.kampane');
      if (discountItems.length > 0) {
        tableHTML += '<br><strong>Aktuálne kampane banky:</strong><ul>';
        discountItems.forEach(item => {
          tableHTML += `<li>${item.textContent.trim()}</li>`;
        });
        tableHTML += '</ul>';
      }

      // Append campaign link if available
      if (campaignLink) {
        tableHTML += `<strong>Zaujala Vás niektorá z kampaní, viac</strong> <a href="${campaignLink}" target="_blank">TU</a>`;
      }

      // Create a Blob with the HTML format
      const blob = new Blob([tableHTML], { type: "text/html" });

      // Create a ClipboardItem object
      const clipboardItem = new ClipboardItem({ "text/html": blob });

      try {
        await navigator.clipboard.write([clipboardItem]);
        this.$store.commit("appState/setSuccessMessage", {
          text: "Ponuka bola skopírovaná",
          type: "success"
        });
      } catch (err) {
        console.error("Nepodarilo sa skopírovať:", err);
      }
    },

    async registerNewApplication(offerObject) {
      let newApplication = await createMortgageApplication(offerObject, "bank");
      newApplication = await this.$store.dispatch("mortgageDetailState/fillMissingApplicationsData", [newApplication]);
      this.$store.commit("mortgageDetailState/registerNewApplication", newApplication[0]);
      await this.$store.dispatch("mortgageDetailCloud/addMissingCloudObject", {applications: newApplication});
    },
    async deleteExistingApplication() {
      const id = this.getIdOfExistingApplication();
      if (!id) return;
      await deleteMortgageApplicationByID(id);
      this.$store.commit("mortgageDetailState/deleteApplication", id)
    },
    async createApplication () {
      const isBankAlreadyCreated = this.$store.getters["mortgageDetailState/isBankApplicationCreated"](this.offer.bank.id);
      this.loading = true;
      const offerObject = this.getApplicationObject();
      for (const possibleRateChange of this.offer.extra?.mozne_zmeny_sadzieb) {
        if (!possibleRateChange.auto_applied && !this.mortgageRateChanges.includes(possibleRateChange.id)) continue;
        offerObject.zmeny_sadzieb.push(possibleRateChange.id)
      }
      if (!isBankAlreadyCreated) {
        await this.registerNewApplication(offerObject);
        const mortgage = this.$store.getters["mortgageDetailState/getMortgage"];
        const mortgageObject = {
          id: mortgage.id,
          object: {
            stage_old: mortgage.stage,
            stage: "kompletizacia_podkladov",
          },
        }
        this.$store.dispatch("mortgageDetailState/patchMortgage", mortgageObject);
      } else {
        this.showDialogToUser();
        const allowed = await this.waitForUserPermission();
        if (allowed) {
          await this.deleteExistingApplication()
          await this.registerNewApplication(offerObject);
        }
      }
      this.loading = false;
      await this.$store.dispatch("mortgageDetailState/fetchMortgage", {force: true});
    },
    getIdOfExistingApplication () {
      const mortgageApplications = this.$store.getters["mortgageDetailState/getMortgage"].applications;
      for (const offer of mortgageApplications) {
        if (offer.bank.code_name !== this.offer.bank.code) continue;
        return offer.id;
      }
      return null;
    },
    getApplicationObject () {
      return {
        mortgage: this.mortgageId,
        bank: this.offer.bank.id,
        splatnost: this.offer.splatnost_r,
        fix: this.offer.fix,
        sadzba_pozadovana: this.offer.sadzba.toFixed(2),
        splatka: this.offer.mesacna_splatka?.toFixed(2),
        poplatok_za_poskytnutie: this.offer.popl_sprac_hypo?.toFixed(2),
        suma_nadobudnutie: this.offer.ucel === "nadobudnutie" ? Math.floor(this.offer.pozicana_suma) : 0,
        suma_refinancovanie: this.offer.ucel === "refinancovanie" ? Math.floor(this.offer.pozicana_suma) : 0,
        suma_bezucel: this.offer.ucel === "bezucel" ? Math.floor(this.offer.pozicana_suma) : 0,
        zmeny_sadzieb: [],
      }
    },
    showDialogToUser () {
      this.$store.commit("appState/setSuccessMessage", {
        text: "Pre danú banku je už vytvorená žiadosť, želáte si ju nahradiť ?",
        type: "confirmation"
      });
    },
    waitForUserPermission () {
      return new Promise((resolve) => {
        const int = setInterval(() => {
          const userConfirmation = this.$store.getters["appState/getUserConfirmation"];
          if (userConfirmation === true) {
            this.$store.commit("appState/setUserConfirmation", null);
            clearInterval(int);
            resolve(true);
          } else if (userConfirmation === false) {
            this.$store.commit("appState/setUserConfirmation", null);
            clearInterval(int);
            resolve(false);
          }
        }, 100);
      });
    },
    openHelperPopup(bankCode) {
      const bankFilterMap = {
        "365": "365.bank",
        "tatra": "Tatra banka",
        "csob": "ČSOB",
        "unicredit": "UniCredit",
        "slsp": "SLSP",
        "vub": "VÚB",
        "prima": "Prima banka",
        "mbank": "mBank",
      };

      this.$store.commit("popupManagerState/pushToPopupOrder", {
        payload: {
          bank: bankFilterMap[bankCode] || null,
        },
        component: "ApplicationHelpPopUp",
        rules: ['single'],
      });
    }
  },
  data: () => ({
    bankNotes: [],
    maxMaturity: 0,
    loading: false,
    mortgageRateChanges: [],
    mortgageId: null,
    bank: null,
    sadzba: 0,
    sadzbaChange: 0,
    amountDraft: 0,
    editingAmount: false,
  }),
};
</script>

<style lang="scss" scoped>

.disabled-maturity {
  filter: invert(94%) sepia(5%) saturate(6%) hue-rotate(332deg) brightness(87%) contrast(82%);
  cursor: not-allowed;
}

.mortgage-result-broker {
  position: relative;
  margin-bottom: 12px;
  padding: 18px 20px;
  border: 1px solid var(--app-border);
  border-radius: 14px;
  background: var(--app-surface);
  color: var(--app-text);
  box-shadow: var(--app-shadow);
}

.mortgage-parameters {
  color: var(--app-text);
  font-size: 12px;
  font-weight: 700;
}

.bank-logo {
  max-height: 38px;
}

.upper-index {
  color: var(--app-text-muted);
  font-size: 8px;
  font-weight: 600;
}

.discount-list {
  gap: 7px;
}

.ltv-90-notice {
  display: flex;
  margin: 12px 0 4px;
  padding: 10px 12px;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid #efb74d;
  border-radius: 9px;
  background: #fff7e3;
  color: #6f4900;
  font-size: 10px;
  line-height: 1.45;
}

.ltv-90-notice strong {
  flex: 0 0 auto;
  font-weight: 900;
}

.ltv-90-notice--missing {
  border-color: var(--app-danger, #c53b3b);
  background: var(--app-danger-soft, #fff0f0);
  color: var(--app-danger, #9d2525);
}

.offer-actions {
  display: flex;
  margin-top: 14px;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.offer-action {
  box-sizing: border-box;
  display: inline-flex;
  min-width: 104px;
  min-height: 40px;
  margin: 0;
  padding: 9px 14px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  background: var(--app-surface-muted);
  color: var(--app-primary);
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none !important;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}

.offer-action:hover {
  border-color: var(--app-primary);
  background: var(--app-primary-soft);
  transform: translateY(-1px);
}

.offer-action--primary {
  border-color: var(--app-primary);
  background: var(--app-primary);
  color: #ffffff !important;
}

.offer-action__loader {
  color: #ffffff;
}

.amount-editor {
  min-height: 36px;
}

.amount-editor__button {
  box-sizing: border-box;
  width: 100%;
  min-height: 36px;
  padding: 4px 6px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  line-height: 1.15;
  text-align: center;
  cursor: text;
}

.amount-editor__button:hover {
  border-color: rgba(45, 130, 218, 0.35);
  background: rgba(45, 130, 218, 0.08);
}

.amount-editor__input {
  box-sizing: border-box;
  width: 100%;
  min-height: 36px;
  padding: 4px 8px;
  border: 2px solid #2d82da;
  border-radius: 8px;
  outline: none;
  background: var(--app-surface);
  color: var(--app-text);
  font: inherit;
  font-weight: inherit;
  line-height: 1.15;
  text-align: center;
}

.error-offer {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.error-message {
  font-size: 13px;
  margin-left: 34px;
}

.error-message:not(:last-child) {
  margin-bottom: 5px;
}

.usetrene{
  font-weight: 500 !important;
  opacity: 0.8 !important;
}

.splatka{
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

</style>
